import dayjs from 'dayjs';
import { getRoutes, ROLES } from '../routes/Routes';
import _, { toLower } from 'lodash';
import { env, images_cdn, netsuite_url, sticky_config } from '../components/config';
import { COLLECTION_END_TIME, COLLECTION_START_TIME, deliveryDays, VAT_RATE } from '../constants';

export const generateReduxKeys = (keys) => {
  const localStorageKeys = Object.keys(localStorage);

  const initialState = {};

  const isValidJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  for (const key of Object.keys(keys)) {
    const generatedKey = key;
    const initialValue = keys[key];

    const keysList = localStorageKeys.filter((key) => key.endsWith(generatedKey));
    if (!keysList.includes(generatedKey)) initialState[generatedKey] = initialValue;

    keysList.forEach((key) => {
      const value = localStorage.getItem(key);

      if (value && isValidJSON(value)) {
        initialState[key] = JSON.parse(value);
      } else {
        initialState[key] = initialValue;
      }
    });
  }

  return initialState;
}

export const handleLogout = () => {
  localStorage.clear();
  window.location.href = getRoutes().login.route();
}

export const getInitials = (name) => {
  if (!name) return '';

  const nameParts = name
    .split(' ')
    .map((part) => part.trim())
    .filter((part) => /^[A-Za-z]/.test(part));

  if (nameParts.length === 0) return '';
  if (nameParts.length === 1) return nameParts[0][0]?.toUpperCase() || '';

  const firstInitial = nameParts[0][0]?.toUpperCase() || '';
  const lastInitial = nameParts[nameParts.length - 1][0]?.toUpperCase() || '';

  return firstInitial + lastInitial;
};

export const getRoleByValue = (value) => {
  const key = Object.keys(ROLES).find((k) => ROLES[k] === value);
  return key ? key.replace(/_/g, ' ') : null;
};

export const formatCurrency = (
  amount,
  {
    locale = 'en-GB',
    currency = 'GBP',
    compact = false,
    maximumFractionDigits = 2,
  } = {}
) => {
  let value;

  if (typeof amount === 'number') {
    value = amount;
  } else if (typeof amount === 'string') {
    value = Number(amount.replace(/[^\d.-]/g, ''));
  } else {
    value = 0;
  }

  value = Number.isNaN(value) ? 0 : value;

  if (Math.abs(value) < 0.005) {
    value = 0;
  }

  if (value === 0) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0);
  }

  let formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: compact ? 'compact' : 'standard',
    compactDisplay: 'short',
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits,
  }).format(value);

  if (compact) {
    formatted = formatted.replace(/k\b/g, 'K')
      .replace(/m\b/g, 'M')
      .replace(/bn\b/g, 'B')
      .replace(/t\b/g, 'T');

  }

  return formatted;
};

export const formatCount = (amount, locale = 'en-GB') => {
  let value =
    typeof amount === 'number'
      ? amount
      : Number(amount?.replace(/[^\d.-]/g, ''));

  value = isNaN(value) ? 0 : value;

  const roundedValue = Number(value.toFixed(2));

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(roundedValue);
};

export const getTimeSlots = (start = COLLECTION_START_TIME, end = COLLECTION_END_TIME, intervalMinutes = 30) => {
  const slots = [];
  let current = dayjs(start, 'HH:mm');
  const endTime = dayjs(end, 'HH:mm');

  while (current.isBefore(endTime) || current.isSame(endTime)) {
    const formatted24 = current.format('HH:mm'); // 09:00
    slots.push(formatted24);
    current = current.add(intervalMinutes, 'minute');
  }

  return slots;
};

export const formatAddress = (item) => {
  if (!item) return { fullAddress: '', formattedAddress: '' };

  const addressLines = Object.keys(item)
    .filter((key) => /^address_\d+$/.test(key) && item[key]?.trim())
    .map((key) => item[key].trim());

  const rawAddress =
    item.address?.trim() || item.fullAddress?.trim() || item.address_1?.trim() || '';

  const clean = (text) =>
    text
      .replace(/\s+/g, ' ')
      .replace(/,+/g, ',')
      .replace(/,\s*,+/g, ',')
      .replace(/,\s*$/, '')
      .trim();

  let fullAddress = '';
  let formattedAddress = '';

  if (addressLines.length) {
    fullAddress = clean([...addressLines, item.city, item.postcode].filter(Boolean).join(', '));
    formattedAddress = clean(
      [item.attention, item.addressee, ...addressLines, item.city, item.postcode]
        .filter(Boolean)
        .join(', ')
    );
  } else if (rawAddress) {
    fullAddress = clean(rawAddress);
    formattedAddress = clean(
      [item.attention, item.addressee, rawAddress].filter(Boolean).join(', ')
    );
  }

  return { fullAddress, formattedAddress };
};

export const getEffectivePrice = (item) => {
  const multiplier = item?.uom?.toLowerCase() === 'case' ? (item?.product_case_quantity || 1) : 1;
  return (Number(item?.unit_price || item?.order_item_unit_price) || 0) * multiplier;
};

export const calculateOrderSummary = (orders) => {
  const subtotal = _.sumBy(orders, (item) => getEffectivePrice(item) * (Number(item.quantity || item.order_item_quantity) || 0));

  const vat = _.sumBy(orders, (item) => {
    const isTaxable = parseInt(item.vat) === 20 || item?.taxable?.toLowerCase() === 'taxable';
    const rawVat = isTaxable ? getEffectivePrice(item) * (Number(item.quantity || item.order_item_quantity) || 0) * VAT_RATE : 0;
    return Math.round(rawVat * 100) / 100;
  });

  const grandTotal = subtotal + vat;

  return {
    subtotal,
    vat,
    grandTotal,
  };
};

export const getProductImageUrl = (product) => {
  if (!product?.profile_image) return null;
  return `${images_cdn}/${env}/warehouse-products/${product?.netsuite_id || product?.product_netsuite_id}/${product?.profile_image}`;
};

export const calculateVAT = ({ unitPrice, quantity, vatField }) => {
  const price = Number(unitPrice) || 0;
  const qty = Number(quantity) || 0;
  const isTaxable = parseInt(vatField) === 20;
  const vatRate = isTaxable ? VAT_RATE : 0;

  const subtotal = price * qty;
  const vatAmount = subtotal * vatRate;
  const totalWithVAT = subtotal + vatAmount;

  return { subtotal, vatAmount, totalWithVAT };
};

export const getAddressType = (addr) => {
  if (!addr) return 'Address';

  const isBilling = addr.default_billing === 1;
  const isShipping = addr.default_shipping === 1;

  if (isBilling && isShipping) return 'Billing & Shipping Address';
  if (isBilling) return 'Billing Address';
  if (isShipping) return 'Shipping Address';

  return 'Other Address';
};

export const getAddressTypeIndicator = (addr) => {
  if (!addr) {
    return {
      isBilling: false,
      isShipping: false,
      isBoth: false,
    };
  }

  const isBilling = addr.default_billing === 1;
  const isShipping = addr.default_shipping === 1;

  const color = isBilling && isShipping ? 'orange-inverse' : isBilling ? 'green-inverse' : isShipping ? 'blue-inverse' : 'gray';

  return {
    isBilling,
    isShipping,
    isBoth: isBilling && isShipping,
    color,
  };
};

export const sortPriceLevels = (levels = []) => {
  if (!Array.isArray(levels)) return [];

  const sorted = _.sortBy(levels, (item) => {
    const match = item.price_level.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : Infinity;
  });

  const numbered = sorted.filter((item) => /^\d+/.test(item.price_level));
  const unnumbered = sorted.filter((item) => !/^\d+/.test(item.price_level));

  return [...numbered, ..._.sortBy(unnumbered, ['price_level'])];
};

export const getFullName = (user) => {
  if (!user) return '';
  if (user.first_name === undefined) return '';
  return `${user.first_name} ${user.last_name || ''}`.trim();
};

export const getDateRangeFormat = (dates) => {
  if (!dates) return { start_date: null, end_date: null };

  const start_date = dates[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
  const end_date = dates[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null;

  return { start_date, end_date };
};

export const dateRangeWithTime = (dates) => {
  if (!dates) return { start_date: null, end_date: null };

  const start_date = dates[0]
    ? dayjs(dates[0])
      .startOf('day')
      .subtract(5, 'hour')
      .format('YYYY-MM-DD HH:mm:ss')
    : null;

  const end_date = dates[1]
    ? dayjs(dates[1])
      .endOf('day')
      .subtract(5, 'hour')
      .format('YYYY-MM-DD HH:mm:ss')
    : null;

  return { start_date, end_date };
};

export const getTierOptions = (record) => {
  if (!record?.tiers?.length) return [];

  const isCase = record?.uom?.toLowerCase() === "case";

  const effectiveQuantity = isCase
    ? record.quantity * record.product_case_quantity
    : record.quantity;

  return _(record.tiers)
    .groupBy("price_level")
    .map((items, priceLevel) => {
      const tier =
        _.chain(items)
          .filter(({ minimum_quantity }) => minimum_quantity <= effectiveQuantity)
          .maxBy("minimum_quantity")
          .value() ??
        _.minBy(items, "minimum_quantity");

      return {
        label: priceLevel,
        value: priceLevel,
        price: isCase
          ? tier?.tier_price * record?.product_case_quantity
          : tier?.tier_price,
      };
    })
    .sortBy(({ value }) => {
      const match = /^Tier\s+(\d+)$/.exec(value);
      return match ? Number(match[1]) : Infinity;
    })
    .value();
};

export const getFormattedPriceLevels = (
  items,
  {
    quantity = 1,
    uom = "Each",
    caseQuantity = 1,
  } = {}
) => {
  if (!items?.length) return [];

  const isCase = uom?.toLowerCase() === "case";

  const effectiveQuantity = isCase
    ? quantity * caseQuantity
    : quantity;

  return _(items)
    .groupBy("price_level")
    .map((levels) => {
      const matched =
        _.chain(levels)
          .filter(({ minimum_quantity }) => minimum_quantity <= effectiveQuantity)
          .maxBy("minimum_quantity")
          .value() ??
        _.minBy(levels, "minimum_quantity");

      return matched;
    })
    .sortBy(({ price_level }) => {
      const match = /^Tier\s+(\d+)$/.exec(price_level);
      return match ? Number(match[1]) : Infinity;
    })
    .value();
};

export const getApplicableTierPrice = (record) => {
  const { tiers = [], quantity = 0, product_case_quantity = 1, uom, price_level } = record;

  if (!tiers.length) return null;

  const isCase = uom?.toLowerCase() === "case";

  const effectiveQuantity = isCase
    ? quantity * product_case_quantity
    : quantity;

  const priceLevelTiers = tiers.filter(
    ({ price_level: level }) => level === price_level
  );

  if (!priceLevelTiers.length) return null;

  return (
    _.chain(priceLevelTiers)
      .filter(({ minimum_quantity }) => minimum_quantity <= effectiveQuantity)
      .maxBy("minimum_quantity")
      .value() ??
    _.minBy(priceLevelTiers, "minimum_quantity")
  );
};

export const getProductRibbon = (product) => {
  const ribbons = [];

  if (product?.promo_price && product?.promo_price <= product?.unit_price) {
    ribbons.push({ text: "Offer", color: "red", hexColor: "#ff4d4f" });
  }

  if (!product?.availability) {
    ribbons.push({ text: "Not Available", color: "volcano", hexColor: "#fa541c" });
  }

  if (product?.on_hold) {
    ribbons.push({ text: "On Hold", color: "orange", hexColor: "#fa8c16" });
  }

  return ribbons;
};

export const formatCustomerDays = (data = {}) => {
  const getDays = (prefix) =>
    deliveryDays.filter((d) => {
      const key = `${prefix}_${d.label.toLowerCase()}`;
      return data[key] === 1;
    });

  return {
    deliveryDays: getDays("delivery"),
    orderDays: getDays("order"),
  };
};

export const getNetsuiteLink = ({ invoiceId, type }) => {
  if (!invoiceId) return null;
  return `${netsuite_url}/app/accounting/transactions/${type}.nl?id=${invoiceId}`;
};

export const getStickyPaymentUrl = ({
  mainUrl,
  amount,
  orderId,
  magnaId,
}) => {

  const appConfig = [
    String(magnaId || ''),
    String(sticky_config?.appId || ''),
    String(orderId || ''),
  ]?.join(':');

  const chpConnectionIndex = localStorage.getItem('chpConnectionIndex') || 0;

  const params = new URLSearchParams({
    setPaymentTotal: String(amount),
    setPaymentUserPaymentId: appConfig,
    chpConnectionIndex: String(chpConnectionIndex),
  });

  return `${mainUrl}&${params.toString()}`;
};

export const downloadFileDirectly = (file_url) => {
  if (!file_url) return;
  const link = document.createElement('a');
  link.href = file_url;
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getBalanceColor = (balance) => {
  const credit = Number(balance?.creditLimit) || 0;
  const bal = Number(balance?.balance) || 0;

  if (bal < 0) return 'green';
  if (bal > credit) return 'red';

  const percentage = credit > 0 ? (bal / credit) * 100 : 0;
  if (percentage >= 80) return 'orange';

  return 'green';
};


export const checkInTimeSpent = (record) => {
  if (!record?.check_in_time || !record?.check_out_time) return '-';
  const diff = dayjs(record.check_out_time).diff(dayjs(record.check_in_time));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (hours > 0) return `${hours}h ${mins}m ${seconds}s`;
  if (mins > 0) return `${mins}m ${seconds}s`;
  return `${seconds}s`;
};

export const generateCustomerSecurityCode = (length = 6) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const getNetSuiteErrorMessage = (info) => {
  try {
    const parsedInfo = _.isString(info) ? JSON.parse(info) : info;
    const errorMessage = _.get(parsedInfo, 'error.message');

    if (!errorMessage) return;

    if (!_.isString(errorMessage)) {
      return _.get(errorMessage, 'message');
    }

    try {
      return _.get(JSON.parse(errorMessage), 'message') || errorMessage;
    } catch {
      return errorMessage;
    }
  } catch {
    return;
  }
};